import {FC} from "react";
import {useTranslation} from "react-i18next";
import {FetchResult, useMutation, useSubscription} from "@apollo/client";
import {useMachine} from "react-robot";
import {BackendError} from "@common-core/runtime-js/backend";
import {CreateOperationalEntityResult} from "@common-core/coat-operational-hierarchy-appsync-model";
import {Endpoints} from "../../../runtime";
import {useFeedback} from "../../../feedback";
import {
  CreateOperationalEntity,
  CreateOperationalEntityData,
  CreateOperationalEntityVariables,
  OnCreateOperationalEntity,
  OnCreateOperationalEntityData,
  OnCreateOperationalEntityVariables
} from "../../backend";
import {usePlan} from "./context";
import {toCreateResult, toStateMachineEvent} from "./transforms";
import {stateMachine} from "./state-machine";
import "./OrganizationBuilder.scss";

export const OrganizationBuilder: FC = () => {
  const {t} = useTranslation();
  const [, setFeedback] = useFeedback();
  const {plan} = usePlan();

  useSubscription<
    OnCreateOperationalEntityData,
    OnCreateOperationalEntityVariables
  >(OnCreateOperationalEntity, {
    variables: {transactionId: plan.input.transactionId!},
    context: {
      endpoint: Endpoints.APPSYNC
    },
    onData: ({data: {data}}) => {
      if (data?.result) {
        send(toStateMachineEvent(plan, toCreateResult(data.result)));
      }
    }
  });

  const [createOperationalEntity] = useMutation<
    CreateOperationalEntityData,
    CreateOperationalEntityVariables
  >(CreateOperationalEntity, {
    context: {
      endpoint: Endpoints.APPSYNC
    },
    fetchPolicy: "network-only"
  });

  const assertResultPresent = (
    result: FetchResult<CreateOperationalEntityData>
  ): CreateOperationalEntityResult => {
    if (!result.data?.entity) {
      throw new BackendError(t("create-organization.errors.no-results"), 500);
    }
    return result.data.entity;
  };

  const createOrganization = (
    variables: CreateOperationalEntityVariables
  ): Promise<void> =>
    createOperationalEntity({variables})
      .then(assertResultPresent)
      .then(toCreateResult)
      .then(result => send(toStateMachineEvent(plan, result)))
      .catch(error => {
        // GraphQL error occurred - send failure event with minimal failed result
        send({
          type: "failure",
          result: {
            sources: plan.input.sources.map(s => ({
              sourceId: s.sourceId,
              sourceType: s.sourceType
            })),
            dbaName: plan.input.organizationInformation.dbaName,
            status: "failed",
            message: error.message || "Creation failed",
            children: []
          },
          source: plan.source
        });
      });

  const [{context}, send] = useMachine(stateMachine, {
    createOrganization,
    translate: t,
    showFeedback: setFeedback,
    clearFeedback: () => setFeedback(undefined),
    create: () => send({type: "create", ...plan}),
    retry: () => send({type: "retry", ...plan}),
    stepBack: () => send({type: "step-back"})
  } as any);

  return (
    <section className="organization-builder">
      {context.stepper()}
      {context.step()}
      {context.controls()}
    </section>
  );
};
